import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { DASH } from "../constants/testIds";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Flame, Target, Sparkles, ArrowRight, PlayCircle, Youtube } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [rec, setRec] = useState(null);
  const [map, setMap] = useState(null);
  const [inProgress, setInProgress] = useState(null);
  const [recent, setRecent] = useState([]);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [r, m, ip, rc] = await Promise.all([
          api.get("/analytics/me/recommendation"),
          api.get("/analytics/me/map"),
          api.get("/sessions/in-progress"),
          api.get("/sessions/recent?limit=5"),
        ]);
        setRec(r.data);
        setMap(m.data);
        setInProgress(ip.data?.session || null);
        setRecent(rc.data || []);
      } catch (e) { console.error(e); }
    })();
  }, []);

  const startMixed = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/sessions/start", {});  // mixed → no topic scope
      nav(`/oturum/${data.session_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Oturum başlatılamadı");
    } finally { setBusy(false); }
  };

  const startTopic = async (topic_id) => {
    setBusy(true);
    try {
      const { data } = await api.post("/sessions/start", { topic_id });
      nav(`/oturum/${data.session_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Konuya özel oturum başlatılamadı");
    } finally { setBusy(false); }
  };

  const openVideo = async (topic_id) => {
    try {
      const { data } = await api.get(`/catalog/topics/${topic_id}/video`);
      if (data.video_url) window.open(data.video_url, "_blank", "noopener,noreferrer");
      else toast.info("Konu anlatım videosu henüz eklenmemiş.");
    } catch { toast.error("Video bilgisi alınamadı"); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-sm text-slate-500">Merhaba, {user?.name?.split(" ")[0] || "Öğrenci"}</div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight mt-1 text-[#0B1A35]">Bugün ne çalışalım?</h1>
      </div>

      {/* Resume in-progress session */}
      {inProgress && (
        <div data-testid="dashboard-resume-card"
             className="relative overflow-hidden rounded-2xl bg-[#FFF8E1] border border-[#FFE082] p-5 sm:p-6 yz-enter">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#9A7B00] font-mono mb-1">Devam Eden Çalışma</div>
              <div className="font-display text-xl text-[#0B1A35]">
                {inProgress.answered_count} / {inProgress.total_questions} soru tamamlandı
              </div>
            </div>
            <button
              data-testid="dashboard-resume-btn"
              onClick={() => nav(`/oturum/${inProgress.session_id}`)}
              className="yz-cta inline-flex items-center gap-2 px-4 py-2.5 rounded-full">
              Kaldığın Yerden Devam Et <ArrowRight size={16}/>
            </button>
          </div>
        </div>
      )}

      <div data-testid={DASH.recommendation}
           className="relative overflow-hidden rounded-2xl yz-hero-panel text-white p-6 sm:p-8 shadow-lg">
        <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-[#FFC107]/20 blur-3xl" />
        <div className="absolute -left-10 -bottom-16 w-56 h-56 rounded-full bg-[#007BFF]/20 blur-3xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 text-[#FFC107] text-sm tracking-wide font-mono mb-2" data-testid="dashboard-recommendation-label">
            <Sparkles size={14}/> Bugünkü Öneri
          </div>
          <div className="font-display text-2xl sm:text-3xl tracking-tight mb-2">
            {rec?.name || "Karışık Çalışma"}
          </div>
          <div className="text-white/70 text-sm mb-6 max-w-lg" data-testid="dashboard-recommendation-explanation">
            {rec?.explanation || rec?.reason || "Sınav kapsamından karışık sorularla ısınabilirsin."}
          </div>
          {/*
            Bugünkü Öneri kartında TAM OLARAK 3 aksiyon:
              1) Önerilen konu için 10 soru
              2) Önerilen konu için YouTube konu anlatım videosu
              3) Karışık 10 soru (tüm kapsam)
            Soru-özel çözüm video butonu buraya EKLENMEZ; oraya sadece
            oturum inceleme sayfasında (SessionReview) yer verilir.
            Recommendation exit-criteria (bu öneri ne zaman biter) Phase 3'te
            mastery / hedef tabanlı olarak tanımlanacak — "artık en zayıf konu değil"
            kriteri kullanılmaz.
          */}
          <div className="flex flex-wrap gap-2">
            {rec?.topic_id && (
              <button onClick={() => startTopic(rec.topic_id)} disabled={busy} data-testid={DASH.startBtn}
                className="yz-cta inline-flex items-center gap-2 px-5 py-3 rounded-full disabled:opacity-60">
                {rec?.name} — 10 Soru Çöz <ArrowRight size={16}/>
              </button>
            )}
            {rec?.topic_id && (
              <button onClick={() => openVideo(rec.topic_id)} data-testid="dashboard-topic-lesson-btn"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/10 hover:bg-white/20 font-medium">
                <Youtube size={16}/> {rec?.name} Konu Anlatımı
              </button>
            )}
            <button onClick={startMixed} disabled={busy} data-testid="dashboard-start-mixed-btn"
              className={`inline-flex items-center gap-2 px-5 py-3 rounded-full disabled:opacity-60 font-medium ${
                rec?.topic_id ? "bg-white/10 hover:bg-white/20" : "yz-cta"
              }`}>
              {busy ? "Hazırlanıyor…" : "Karışık 10 Soru Çöz"} <ArrowRight size={16}/>
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard tid={DASH.overallAccuracy} label="Genel Başarı" value={`${map?.overall_accuracy ?? 0}%`} icon={<Target size={18}/>}/>
        <StatCard tid="dashboard-recent-accuracy" label="Yakın Zaman" value={map?.recent_accuracy != null ? `${map.recent_accuracy}%` : "—"} icon={<Flame size={18}/>}/>
        <StatCard tid={DASH.totalQuestions} label="Çözülen Soru" value={map?.total_questions ?? 0} icon={<PlayCircle size={18}/>}/>
        <StatCard tid={DASH.totalSessions} label="Oturum" value={map?.total_sessions ?? 0} icon={<Sparkles size={18}/>}/>
      </div>

      {/* Last sessions */}
      {recent.length > 0 && (
        <section data-testid="dashboard-recent-sessions">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-display tracking-tight">Son Çalışmalarım</h2>
          </div>
          <div className="grid gap-2">
            {recent.map((s) => (
              <div key={s.session_id}
                className="bg-white rounded-xl border border-[#E2E8F0] p-3 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-xs text-slate-500 font-mono">
                    {new Date(s.started_at || s.completed_at || Date.now()).toLocaleDateString("tr-TR", { day: "2-digit", month: "short" })}
                    {" · "}{s.total_questions} soru
                    {s.topic_name && <> · {s.topic_name}</>}
                  </div>
                  <div className="text-sm mt-0.5">
                    {s.status === "IN_PROGRESS" ? (
                      <span className="text-[#007BFF]">Devam Ediyor · {s.answered_count}/{s.total_questions}</span>
                    ) : (
                      <>
                        <span className="text-emerald-600">{s.correct} Doğru</span>
                        <span className="text-slate-400"> · </span>
                        <span className="text-rose-600">{s.wrong} Yanlış</span>
                        {s.blank > 0 && <>
                          <span className="text-slate-400"> · </span>
                          <span className="text-slate-500">{s.blank} Boş</span>
                        </>}
                      </>
                    )}
                  </div>
                </div>
                <button
                  data-testid={`dashboard-session-review-${s.session_id}`}
                  onClick={() => nav(s.status === "IN_PROGRESS" ? `/oturum/${s.session_id}` : `/inceleme/${s.session_id}`)}
                  className="px-3 py-1.5 rounded-lg text-sm border border-[#E2E8F0] hover:border-[#007BFF] hover:text-[#007BFF] transition-colors">
                  {s.status === "IN_PROGRESS" ? "Devam" : "İncele"}
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-display tracking-tight">Öncelikli Konular</h2>
          <button onClick={() => nav("/harita")} className="text-sm text-[#007BFF] hover:underline">Haritayı gör</button>
        </div>
        <div className="grid gap-3">
          {(map?.topics || []).slice(0, 4).map((t) => (
            <div key={t.topic_id} className="yz-card p-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-medium text-[#0B1A35]">{t.name}</div>
                <div className="text-xs text-slate-500">{t.attempts} soru yanıtlandı</div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <div className="w-24 h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className={`h-full ${t.band === "strong" ? "bg-emerald-500" : t.band === "developing" ? "bg-[#FFC107]" : "bg-rose-500"}`}
                       style={{ width: `${t.accuracy}%` }}/>
                </div>
                <div className="text-sm font-mono w-10 text-right">{t.accuracy}%</div>
                {t.recent_accuracy != null && (
                  <div className="text-xs text-slate-500 font-mono" data-testid={`dashboard-topic-recent-${t.topic_id}`}>
                    Yakın: <span className="text-[#007BFF]">{t.recent_accuracy}%</span>
                  </div>
                )}
                <button
                  data-testid={`dashboard-topic-practice-${t.topic_id}`}
                  onClick={() => startTopic(t.topic_id)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs bg-[#0B1A35] text-white hover:bg-[#16294D] transition-colors">
                  <PlayCircle size={14}/> Konuya Özel 10 Soru
                </button>
                <button
                  onClick={() => openVideo(t.topic_id)}
                  className="yz-secondary inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs">
                  <Youtube size={14}/> Konu Anlatımı
                </button>
              </div>
            </div>
          ))}
          {(!map?.topics || map.topics.length === 0) && (
            <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-xl p-6 text-center">
              Henüz çözülmüş soru yok. Yukarıdaki <b>Karışık 10 Soru</b> ile başla.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function StatCard({ label, value, icon, tid }) {
  return (
    <div data-testid={tid} className="yz-card p-4">
      <div className="flex items-center justify-between text-slate-500 text-xs mb-2">
        <span>{label}</span>
        <span className="grid place-items-center w-7 h-7 rounded-full bg-[#FFF3C4] text-[#9A7B00]">{icon}</span>
      </div>
      <div className="font-display text-2xl text-[#0B1A35]">{value}</div>
    </div>
  );
}
